import io from "socket.io-client";
import api from "../services/api";

// =====================================================================
// SocketWorker (S40 fix #51 + #69 — v2 com defesas em 3 camadas)
//
// HISTORICO:
// - S39 #47: socket reconnect/CORS arrumado (CRM 2.5.0)
// - S39 #48: refactor 150 linhas REVERTIDO (loop/RAM esgotada)
// - S39 #50: backend CORS multi-origin OK
// - S40 #51 v1 (~00:50 28/05): fix singleton stale token undefined.
//   DEPLOY APLICADO mas REVERTIDO ~01:00 por causa de bug #69 revelado.
// - S40 #69 v2 (LOCAL): defesas adicionais pra companyId=undefined
//   + max retry "io server disconnect" pra prevenir loop infinito.
//
// BUGS PRE-EXISTENTES atacados nesta versao:
//   #51 — singleton captura token=undefined no primeiro new (pre-login)
//   #69 — companyId=undefined cria URL "/undefined" no namespace
//   #69b — handler "io server disconnect" auto-reconecta sem limite,
//          gerando loop infinito quando backend continua rejeitando.
//
// DEFESAS APLICADAS:
//   1. Guard token: retry com backoff (max 5s/10 tentativas)
//   2. Guard companyId: NAO instancia se companyId falsy (return)
//   3. Refresh token em connect() (token pode mudar com refresh JWT)
//   4. Max reconnect attempts em "io server disconnect" (5 tentativas)
//      depois dispara custom event pra UI mostrar erro.
//   5. Detect loop: se >3 disconnects em 10s, para auto-reconnect.
// =====================================================================

const MAX_SERVER_DISCONNECT_RETRIES = 5;
const DISCONNECT_LOOP_THRESHOLD = 3; // disconnects em 10s = loop
const DISCONNECT_LOOP_WINDOW_MS = 10_000;

class SocketWorker {
  constructor(companyId, userId) {
    // S40 #69: GUARD CRITICO — sem companyId valido, URL fica "/undefined"
    // Esse mesmo guard EXISTE no socketConnection() em socket.js (1a camada).
    // Aqui e a 2a camada (defesa em profundidade).
    if (!companyId || companyId === "undefined" || companyId === "null") {
      console.warn(
        "[SocketWorker] companyId invalido (" + companyId + ") — NAO instanciando socket"
      );
      // Retorna instance vazia que NAO conecta — caller pode tentar de novo depois
      return {
        companyId: null,
        userId: null,
        socket: null,
        on: () => {},
        emit: () => {},
        off: () => {},
        disconnect: () => {},
        connect: () => {},
        forceReconnect: () => {}
      };
    }

    const sessionToken = api.defaults.headers.Authorization;

    if (SocketWorker.instance) {
      const sameSession =
        SocketWorker.instance.companyId === companyId &&
        SocketWorker.instance.userId === userId;
      if (sameSession) {
        // S40 #51: atualizar token AQUI antes de retornar singleton
        const freshToken = api.defaults.headers.Authorization;
        if (freshToken && freshToken !== "Bearer undefined") {
          SocketWorker.instance.token = freshToken;
        }
        return SocketWorker.instance;
      }
      // Usuario diferente — invalida singleton velho
      try {
        SocketWorker.instance.disconnect();
      } catch {}
      SocketWorker.instance = null;
    }

    this.companyId = companyId;
    this.userId = userId;
    this.token = sessionToken;
    this.socket = null;
    this.eventListeners = {};
    this.intentionalDisconnect = false;
    this._configureAttempts = 0;
    this._serverDisconnectCount = 0; // S40 #69: counter pra loop detection
    this._serverDisconnectWindowStart = 0;
    this._gaveUp = false; // S53: stop STICKY apos loop/mismatch persistente (so forceReconnect/re-login limpa)
    this.configureSocket();
    SocketWorker.instance = this;
  }

  configureSocket() {
    // S40 #51: re-ler token caso tenha sido undefined no constructor
    if (!this.token || this.token === "Bearer undefined") {
      this.token = api.defaults.headers.Authorization;
    }

    // S40 #51: retry com backoff se token ainda undefined (max 10 tentativas = 5s)
    if (!this.token || this.token === "Bearer undefined") {
      if (this._configureAttempts < 10) {
        this._configureAttempts++;
        console.warn(
          `[Socket] Token ainda undefined, retry ${this._configureAttempts}/10 em 500ms`
        );
        setTimeout(() => this.configureSocket(), 500);
        return;
      }
      console.error(
        "[Socket] Token undefined apos 10 retries (5s). Abortando configure."
      );
      return;
    }

    // Cleanup defensivo
    if (this.socket) {
      try {
        this.socket.removeAllListeners();
        this.socket.disconnect();
      } catch {}
    }

    this.socket = io(
      `${process.env.REACT_APP_BACKEND_URL}/${this.companyId}`,
      {
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 15, // S53: teto (era Infinity) -> dispara reconnect_failed -> banner em vez de martelar pra sempre
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,
        timeout: 20000,
        query: { userId: this.userId, token: this.token }
      }
    );

    this.socket.on("connect", () => {
      console.log("[Socket] Connected:", this.socket.id);
      this._configureAttempts = 0;
      // S40 #69: reseta contador de disconnects ao conectar com sucesso
      this._serverDisconnectCount = 0;
      this._gaveUp = false; // S53: conexao OK -> auto-cura o estado de desistencia
    });

    this.socket.on("disconnect", (reason) => {
      console.warn("[Socket] Disconnected:", reason);

      // S40 #69: detecta loop de "io server disconnect"
      if (reason === "io server disconnect" && !this.intentionalDisconnect) {
        const now = Date.now();
        if (now - this._serverDisconnectWindowStart > DISCONNECT_LOOP_WINDOW_MS) {
          // Reset janela
          this._serverDisconnectCount = 0;
          this._serverDisconnectWindowStart = now;
        }
        this._serverDisconnectCount++;

        if (this._serverDisconnectCount > DISCONNECT_LOOP_THRESHOLD) {
          console.error(
            `[Socket] LOOP DETECTADO: ${this._serverDisconnectCount} disconnects ` +
            `em <${DISCONNECT_LOOP_WINDOW_MS}ms. Parando auto-reconnect.`
          );
          this._gaveUp = true; // S53: STICKY — impede re-arme do storm via connect()/on()/emit()
          // Notifica UI (banner pode aparecer)
          try {
            window.dispatchEvent(new CustomEvent("socket-loop-detected", {
              detail: { reason, count: this._serverDisconnectCount }
            }));
          } catch {}
          return; // NAO tenta reconectar
        }

        if (this._serverDisconnectCount <= MAX_SERVER_DISCONNECT_RETRIES) {
          try {
            this.socket.connect();
          } catch {}
        }
      }
    });

    this.socket.on("connect_error", (err) => {
      console.warn("[Socket] connect_error:", err && err.message);
    });

    this.socket.on("reconnect", (attempt) => {
      console.log(`[Socket] Reconnected on attempt ${attempt}`);
    });

    this.socket.on("reconnect_failed", () => {
      console.error("[Socket] reconnect_failed — recarregue (Ctrl+F5)");
      this._gaveUp = true; // S53: desistiu apos o teto de tentativas (era Infinity = nunca chegava aqui)
      try {
        window.dispatchEvent(new CustomEvent("socket-reconnect-failed"));
      } catch {}
    });

    if (!SocketWorker._onlineHandlerInstalled) {
      window.addEventListener("online", () => {
        const inst = SocketWorker.instance;
        if (inst && inst.socket && !inst.socket.connected) {
          console.log("[Socket] Browser online — forcing reconnect");
          try {
            inst.socket.connect();
          } catch {}
        }
      });
      SocketWorker._onlineHandlerInstalled = true;
    }
  }

  on(event, callback) {
    this.connect();
    if (!this.socket) return;
    this.socket.off(event, callback);
    this.socket.on(event, callback);

    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    if (this.eventListeners[event].indexOf(callback) === -1) {
      this.eventListeners[event].push(callback);
    }
  }

  emit(event, data) {
    this.connect();
    if (!this.socket) return;
    this.socket.emit(event, data);
  }

  off(event, callback) {
    if (!this.socket) return;
    if (this.eventListeners[event]) {
      if (callback) {
        this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter(
          (cb) => cb !== callback
        );
      } else {
        this.eventListeners[event].forEach((cb) =>
          this.socket.off(event, cb)
        );
        delete this.eventListeners[event];
      }
    }
  }

  disconnect() {
    this.intentionalDisconnect = true;
    if (this.socket) {
      try {
        this.socket.removeAllListeners();
        this.socket.disconnect();
      } catch {}
      this.socket = null;
    }
    this.eventListeners = {};
    SocketWorker.instance = null;
    console.log("[Socket] disconnected manually (logout/switch)");
  }

  connect() {
    // S53: se desistimos (loop/mismatch persistente ou reconnect_failed), NAO re-arma o storm.
    // So forceReconnect() (acao explicita) ou um novo SocketWorker (re-login) reconecta.
    if (this._gaveUp) return;
    const freshToken = api.defaults.headers.Authorization;
    if (freshToken && freshToken !== "Bearer undefined" && freshToken !== this.token) {
      this.token = freshToken;
      if (this.socket) {
        try {
          this.socket.removeAllListeners();
          this.socket.disconnect();
        } catch {}
        this.socket = null;
      }
    }

    if (!this.socket) {
      this.intentionalDisconnect = false;
      this.configureSocket();
      return;
    }
    if (this.socket.disconnected) {
      try {
        this.socket.connect();
      } catch {}
    }
  }

  forceReconnect() {
    this.intentionalDisconnect = false;
    this._configureAttempts = 0;
    this._serverDisconnectCount = 0;
    this._gaveUp = false; // S53: acao explicita (re-login) limpa a desistencia sticky
    this.configureSocket();
  }
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
